import React, { useState, useEffect } from 'react';
import { useOwners } from '../hooks/useOwners';
import { filterOwnersBySearch } from '../utils/ownerUtils';

const OwnerForm = ({ onSubmit, onCancel, loading, editingOwner = null }) => {
    const {
        owners,
        loading: ownersLoading,
        error: ownersError,
        validationErrors,
        createOwner,
        updateOwner,
        deleteOwner,
        clearError
    } = useOwners();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: ''
    });

    const [localError, setLocalError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [editingOwnerId, setEditingOwnerId] = useState(null);

    // Pre-fill form when editing
    useEffect(() => {
        if (editingOwner) {
            setFormData({
                name: editingOwner.name || '',
                email: editingOwner.email || '',
                phone: editingOwner.phone || '',
                address: editingOwner.address || ''
            });
            setEditingOwnerId(editingOwner._id);
        }
    }, [editingOwner]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear errors when user starts typing
        if (validationErrors[name]) {
            clearError();
        }
        if (localError) {
            setLocalError('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLocalError('');
        clearError();

        try {
            let result;
            if (editingOwner || editingOwnerId) {
                result = await updateOwner(editingOwner?._id || editingOwnerId, formData);
            } else {
                result = await createOwner(formData);
            }

            resetForm();
            if (onSubmit) {
                onSubmit(result);
            }
        } catch (err) {
            if (err.message !== 'Validation failed') {
                setLocalError(err.message || 'Error al guardar propietario');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            email: '',
            phone: '',
            address: ''
        });
        setEditingOwnerId(null);
        clearError();
        setLocalError('');
    };

    const handleCancel = () => {
        resetForm();
        if (onCancel) {
            onCancel();
        }
    };

    // Handle edit owner from list
    const handleEditOwner = (owner) => {
        setFormData({
            name: owner.name || '',
            email: owner.email || '',
            phone: owner.phone || '',
            address: owner.address || ''
        });
        setEditingOwnerId(owner._id);
        clearError();
        setLocalError('');
    };

    // Handle delete owner
    const handleDeleteOwner = async (ownerId, ownerName) => {
        if (!window.confirm(`¿Eliminar al propietario "${ownerName}"?`)) {
            return;
        }

        try {
            await deleteOwner(ownerId);
            if (editingOwnerId === ownerId) {
                resetForm();
            }
        } catch (err) {
            setLocalError(err.message || 'Error al eliminar propietario');
        }
    };

    const displayError = localError || ownersError;
    const filteredOwners = filterOwnersBySearch(owners, searchTerm);
    const currentEditingOwner = editingOwnerId ? owners.find(o => o._id === editingOwnerId) : editingOwner;

    return (
        <div className="owner-form">
            <h3>{currentEditingOwner ? 'Editar propietario' : 'Nuevo propietario'}</h3>
            <p className="form-description">
                {currentEditingOwner ? 'Actualiza la información del propietario.' : 'Agrega un nuevo propietario al sistema.'}
            </p>

            {displayError && (
                <div className="error-message">
                    {displayError}
                </div>
            )}
            
            <form onSubmit={handleSubmit}>
                <div className="form-grid">
                    {/* First Row: Name and Phone */}
                    <div className="form-group">
                        <label>Nombre completo *</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Ingresa el nombre completo"
                            required
                            className={validationErrors.name ? 'error' : ''}
                        />
                        {validationErrors.name && (
                            <div className="error">{validationErrors.name}</div>
                        )}
                    </div>

                    <div className="form-group">
                        <label>Teléfono *</label>
                        <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="Ingresa el teléfono"
                            required
                            className={validationErrors.phone ? 'error' : ''}
                        />
                        {validationErrors.phone && (
                            <div className="error">{validationErrors.phone}</div>
                        )}
                    </div>

                    {/* Second Row: Email and Address */}
                    <div className="form-group">
                        <label>Correo electrónico *</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="Ingresa el correo"
                            required
                            className={validationErrors.email ? 'error' : ''}
                        />
                        {validationErrors.email && (
                            <div className="error">{validationErrors.email}</div>
                        )}
                    </div>

                    <div className="form-group">
                        <label>Dirección *</label>
                        <input
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleInputChange}
                            placeholder="Ingresa la dirección"
                            maxLength={60}
                            required
                            className={validationErrors.address ? 'error' : ''}
                        />
                        {validationErrors.address && (
                            <div className="error">{validationErrors.address}</div>
                        )}
                        <div className="character-count">
                            {formData.address.length}/60 caracteres
                        </div>
                    </div>
                </div>

                <div className="owner-form-preview">
                    {(formData.name || formData.email || formData.phone || formData.address) && (
                        <div className="form-preview-section">
                            <h4>👤 Vista previa</h4>
                            <div className="preview-content">
                                {formData.name && <p><strong>Nombre:</strong> {formData.name}</p>}
                                {formData.email && <p><strong>Correo:</strong> {formData.email}</p>}
                                {formData.phone && <p><strong>Teléfono:</strong> {formData.phone}</p>}
                                {formData.address && <p><strong>Dirección:</strong> {formData.address}</p>}
                            </div>
                        </div>
                    )}
                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="btn-secondary"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn-primary"
                        disabled={loading || ownersLoading}
                    >
                        {loading || ownersLoading ? 'Guardando...' : (currentEditingOwner ? 'Actualizar propietario' : 'Agregar propietario')}
                    </button>
                </div>
            </form>

            {/* Existing Owners List */}
            <div className="owners-list-section">
                <div className="owners-list-header">
                    <h4>📋 Propietarios registrados ({owners.length})</h4>
                    <div className="owners-search">
                        <input
                            type="text"
                            placeholder="Buscar propietarios..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                    </div>
                </div>

                {ownersLoading ? (
                    <div className="loading">Cargando propietarios...</div>
                ) : filteredOwners.length === 0 ? (
                    <div className="no-owners">
                        {searchTerm ? (
                            <p>Ningún propietario coincide con tu búsqueda.</p>
                        ) : (
                            <p>No hay propietarios aún. ¡Agrega el primero!</p>
                        )}
                    </div>
                ) : (
                    <div className="owners-grid">
                        {filteredOwners.map(owner => (
                            <div
                                key={owner._id}
                                className={`owner-card ${editingOwnerId === owner._id ? 'editing' : ''}`}
                            >
                                <div className="owner-info">
                                    <h5>{owner.name}</h5>
                                    <p><strong>Correo:</strong> {owner.email}</p>
                                    <p><strong>Teléfono:</strong> {owner.phone}</p>
                                    {owner.address && (
                                        <p><strong>Dirección:</strong> {owner.address}</p>
                                    )}
                                </div>
                                <div className="owner-actions">
                                    <button
                                        onClick={() => handleEditOwner(owner)}
                                        className="edit-owner-btn"
                                        disabled={loading || ownersLoading}
                                        title="Editar propietario"
                                    >
                                        Editar
                                    </button>
                                    <button
                                        onClick={() => handleDeleteOwner(owner._id, owner.name)}
                                        className="delete-owner-btn"
                                        disabled={loading || ownersLoading}
                                        title="Eliminar propietario"
                                    >
                                        Eliminar
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OwnerForm;